import React from 'react';
import { BadgeCheck, Clock, UserPlus, Users } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { useOnboardingTeamMetrics } from '../../../../services/teamMetrics';
import {
  Avatar,
  Callout,
  Card,
  Delta,
  FlipCard,
  FlipHint,
  GoalPill,
  HUES,
  HeroCard,
  Ring,
  SURFACE,
  Skeleton,
  StatTile,
  StatsBack,
  Th,
  personColors,
} from './premium';
import { MiniBars, TrendChart, fmtDaysV, labelsFor } from './teamCharts';
import FunnelFilters from '../dashboard/FunnelFilters';
import CallsCard from './CallsCard';
import { BonusLadder, sourceName, fmtDays, fmtPct, periodLabel, periodNote, vsText, vsTitle } from './teamUi';

// Onboarding Team (Nic 2026-10-01, the Envoy look 2026-10-25): FOTCs per
// specialist (a signed agreement plus every checklist item done = Ready for
// a Claims Manager), how long it takes, whether the team keeps up with the
// clients coming in, where clients stall, VA.gov three ways, and quarterly
// bonus progress.

const TINT = {
  sky: 'bg-gradient-to-br from-sky-400 to-sky-500 text-white shadow-sm shadow-sky-500/25',
  emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-sm shadow-emerald-500/25',
  violet: 'bg-gradient-to-br from-violet-400 to-violet-500 text-white shadow-sm shadow-violet-500/25',
  orange: 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-sm shadow-orange-500/25',
};

// One color per answer on every bar and in the one legend.
const VA_CLASS = {
  yes: 'bg-emerald-500',
  no: 'bg-amber-500',
  unsure: 'bg-sky-400',
  unknown: 'bg-slate-300 dark:bg-white/15',
};
const VA_ROWS = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
  { key: 'unsure', label: "Didn't know" },
  { key: 'unknown', label: 'Not asked' },
];

// One reading of the VA.gov comparison as a bar of the same clients
// (Option A, Nic 2026-11-02): three of these share one scale and one
// legend, counts inside the segments when they fit and on hover always.
const VaBar = ({ title, sub, buckets, rows }) => {
  const total = rows.reduce((a, r) => a + (buckets[r.key] || 0), 0);
  return (
    <div className="grid gap-1.5 sm:grid-cols-[11rem_1fr] sm:items-center">
      <div className="text-xs font-semibold text-slate-800 dark:text-white">
        {title}
        <span className="block text-[10.5px] font-normal text-slate-400">{sub}</span>
      </div>
      <div
        className="flex h-[18px] overflow-hidden rounded-md bg-slate-100 dark:bg-white/10"
        role="img"
        aria-label={rows.map((r) => `${buckets[r.key] || 0} ${r.label.toLowerCase()}`).join(', ')}
      >
        {total
          ? rows.map((r) => {
              const n = buckets[r.key] || 0;
              if (!n) return null;
              return (
                <span
                  key={r.key}
                  title={`${n} ${r.label.toLowerCase()}`}
                  className={cn(
                    'grid place-items-center overflow-hidden whitespace-nowrap text-[10.5px] font-bold',
                    r.key === 'unknown' ? 'text-slate-600 dark:text-slate-300' : 'text-white',
                    VA_CLASS[r.key],
                  )}
                  style={{ flex: `${n} 1 0%`, minWidth: 6 }}
                >
                  {n / total >= 0.08 ? n : ''}
                </span>
              );
            })
          : null}
      </div>
    </div>
  );
};

const OnboardingTeamTab = ({ period, filter, onFilter, onWindow, jumped }) => {
  const { data, isLoading, isError } = useOnboardingTeamMetrics(period, filter);
  // The Source menu (Nic 2026-10-25), the Funnel's own, kept across tabs.
  // Bonus progress (and the Sales tab's time away) always count every source.
  const filtered = !!(filter?.source || filter?.partnerId);
  const srcName = sourceName(filter, data?.filterOptions);
  const sourceRow = onFilter ? (
    <FunnelFilters
      showExport={false}
      source={filter?.source || ''}
      partnerId={filter?.partnerId || ''}
      filterOptions={data?.filterOptions}
      onFilter={onFilter}
      onWindow={onWindow}
      jumped={jumped}
    />
  ) : null;
  const team = data?.team;
  const prev = data?.previous;
  const va = data?.vaGov;
  const series = data?.series || [];
  // Live hot seats, the Sales goal (80%), as the pill's target here too.
  const hotSeatTarget = data?.targets?.hotSeatPct ?? 80;
  const people = data?.specialists || [];
  const colors = personColors(people);
  const p = data?.period;
  const vs = vsText(p);
  const vsT = vsTitle(p);
  // How the charts bucket this period (the server's unit): day, week or month.
  const unit = data?.period?.unit || 'month';
  const labels = labelsFor(unit);
  const by = `by ${unit}`;

  if (isError) {
    return (
      <Card className="py-14 text-center">
        <span className="text-sm text-slate-400">The Onboarding Team numbers couldn&apos;t be loaded.</span>
      </Card>
    );
  }
  if (isLoading || !team) {
    return (
      <div className="space-y-4">
        {sourceRow}
        <Skeleton className="h-56" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[10.25rem]" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const hotTotal = team.hotSeatLive + team.hotSeatCallback;
  const livePct = hotTotal ? (team.hotSeatLive / hotTotal) * 100 : null;
  const inNow = team.working + team.pool;
  const net = team.signed - team.fotcs;
  // FOTCs, full vs credited (Nic 2026-10-26), stacked on the card's back.
  const FOTC_KIND_FIELDS = [
    { key: 'fotc_full', name: 'Full', color: HUES.emerald, get: (b) => (b.fotcs || 0) - (b.fotcsCredited || 0) },
    { key: 'fotc_credited', name: 'Credited', color: '#6EE7B7', get: (b) => b.fotcsCredited || 0 },
  ];
  const stuckMax = Math.max(1, ...(data.stuckOn || []).map((s) => s.clients));

  return (
    <div className="space-y-4">
      {sourceRow}
      <FlipCard
        label="Onboarding Team scoreboard"
        resetKey={period}
        front={
          <HeroCard hint={<FlipHint />} glow="violet">
            <div className="flex flex-col items-center gap-6 text-center lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-8 lg:text-left">
              <div className="lg:justify-self-start">
                <h2 className="mb-0 text-2xl font-bold text-white">Onboarding Team</h2>
                <div className="mt-1 text-sm leading-relaxed text-slate-500">
                  {periodLabel(period)}
                  {srcName ? ` · ${srcName}` : ''}
                  <br />
                  {periodNote(p)}
                </div>
              </div>
              <div className="flex items-start justify-center gap-8 lg:justify-self-center">
                <Ring
                  value={livePct}
                  label="Hot seats live"
                  sub={hotTotal ? `${team.hotSeatLive} of ${hotTotal} answered` : 'none yet'}
                />
                <Ring
                  value={va?.pct?.hadAccess}
                  label="Had VA.gov"
                  sub="on the onboarding call"
                />
              </div>
              <div className="lg:justify-self-end lg:pr-6 lg:text-right">
                <div className="text-5xl font-bold leading-none tabular-nums text-white xl:text-6xl">
                  {team.fotcs.toLocaleString('en-US')}
                </div>
                <div className="mt-2.5 text-sm font-semibold uppercase tracking-wider text-slate-400">
                  FOTCs completed
                </div>
                <div className="mt-2 flex flex-col items-center gap-1 lg:items-end">
                  <Delta value={team.fotcs} prev={prev?.fotcs} vs={vs} title={vsT} dark />
                  <span className="text-sm tabular-nums text-slate-500">
                    median {fmtDays(team.medianDaysToFotc)} from signing · {inNow} in onboarding now
                  </span>
                </div>
              </div>
            </div>
          </HeroCard>
        }
        back={
          <HeroCard hint={<FlipHint close />} glow="violet">
            <div className="flex h-full flex-col">
              <h2 className="mb-0 text-base font-bold text-white">How it&apos;s counted</h2>
              {/* Short and scannable (Nic 2026-10-26: it got busy and ran into the
                  bottom): one line per number, the comparison note once. */}
              <div className="mx-auto grid w-full max-w-3xl flex-1 content-center gap-x-10 gap-y-3 sm:grid-cols-3">
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">FOTC</div>
                  <div className="text-[13px] leading-snug text-slate-400">A client&apos;s first time Ready for a Claims Manager, credited to their specialist.</div>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">Hot seats live</div>
                  <div className="text-[13px] leading-snug text-slate-400">Transfers they answered vs ones that became a callback. RingCentral decides.</div>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">Had VA.gov</div>
                  <div className="text-[13px] leading-snug text-slate-400">Of the clients checked on their onboarding call, how many could already sign in.</div>
                </div>
              </div>
              <p className="mb-0 mt-2 text-center text-[11.5px] text-slate-500">Changes are {vsT.toLowerCase()}.</p>
            </div>
          </HeroCard>
        }
      />

      <div>
        <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
          {periodLabel(period)}
          <span className="ml-2 normal-case tracking-normal text-slate-300">Click a tile to see it {by}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile
            flipKey={period}
            icon={UserPlus}
            tint={TINT.sky}
            accent="text-sky-600"
            label="Clients signed"
            value={team.signed}
            info="Clients who signed their agreement for the first time in this period: the work coming in."
            foot={<Delta value={team.signed} prev={prev?.signed} vs={vs} title={vsT} />}
            backCaption={by}
            back={<MiniBars series={series} short={labels.short} long={labels.long} fields={[{ key: 'signed', name: 'Signed', color: HUES.sky, get: (b) => b.signed }]} />}
          />
          <StatTile
            flipKey={period}
            icon={BadgeCheck}
            tint={TINT.emerald}
            accent="text-emerald-600"
            label="FOTCs completed"
            value={team.fotcs}
            info={`Clients whose checklist was done for the first time, credited to their specialist. Full: everything checked off. Credited: everything but their DD-214 and/or private treatment records, which they're getting; it turns full in place when they come in. Both count, the bonus too.${team.fotcs ? ` This period: ${team.fotcs - (team.fotcsCredited || 0)} full, ${team.fotcsCredited || 0} credited.` : ''}`}
            foot={<Delta value={team.fotcs} prev={prev?.fotcs} vs={vs} title={vsT} />}
            // The back: full vs credited (Nic 2026-10-26).
            backCaption={`${by}, full and credited`}
            back={<MiniBars series={series} short={labels.short} long={labels.long} fields={FOTC_KIND_FIELDS} />}
          />
          <StatTile
            flipKey={period}
            icon={Clock}
            tint={TINT.violet}
            accent="text-violet-600"
            label="Signed to FOTC"
            value={fmtDays(team.medianDaysToFotc)}
            info="The median time from a client signing to their FOTC, for the FOTCs in this period. Lower is better."
            foot={
              <Delta
                value={team.medianDaysToFotc}
                prev={prev?.medianDaysToFotc}
                kind="days"
                invert
                vs={vs}
                title={vsT}
              />
            }
            backCaption={`median ${by}`}
            back={
              <MiniBars
                series={series} short={labels.short} long={labels.long}
                fields={[
                  {
                    key: 'medianDaysToFotc',
                    name: 'Median',
                    color: HUES.violet,
                    get: (b) => b.medianDaysToFotc,
                    format: fmtDaysV,
                  },
                ]}
              />
            }
          />
          <StatTile
            flipKey={period}
            icon={Users}
            tint={TINT.orange}
            accent="text-orange-600"
            label="In onboarding now"
            value={inNow}
            info="Right now, whatever period is picked: clients being worked, waiting to be taken, waiting on VA.gov, and waiting on just their PTRs (private treatment records)."
            foot={<span>right now</span>}
            backCaption="right now"
            back={
              <StatsBack
                stats={[
                  { label: 'being worked', value: team.working, color: HUES.emerald },
                  { label: 'waiting to be taken', value: team.pool, color: HUES.orange },
                  { label: 'on VA.gov', value: team.waitingOnVaGov, color: HUES.amber },
                  // Waiting on PTRs (Nic 2026-10-26): all that's left.
                  { label: `on PTRs${team.waitingOnPtrs ? ` · median ${fmtDays(team.ptrsMedianDays)}` : ''}`, value: team.waitingOnPtrs || 0, color: HUES.violet },
                ]}
              />
            }
          />
        </div>
      </div>

      {/* Keeping up: clients in against FOTCs out. */}
      <Card
        eyebrow={`Keeping up · ${by}`}
        title="Clients signed vs FOTCs completed"
        info="Blue is the work coming in (clients signing), green is the work going out (FOTCs). When blue keeps outrunning green, the team is falling behind. Today's bars are lighter: they're still filling in."
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[13rem_1fr]">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 lg:grid-cols-1 lg:content-start">
            <Callout color={HUES.sky} label="Clients signed" value={team.signed.toLocaleString('en-US')}>
              <Delta value={team.signed} prev={prev?.signed} vs={vs} title={vsT} />
            </Callout>
            <Callout color={HUES.emerald} label="FOTCs completed" value={team.fotcs.toLocaleString('en-US')}>
              <Delta value={team.fotcs} prev={prev?.fotcs} vs={vs} title={vsT} />
            </Callout>
            <Callout
              color={net > 0 ? HUES.amber : HUES.emerald}
              label="Came in vs went out"
              value={net > 0 ? `+${net}` : String(net)}
            >
              {net > 0
                ? `${net} more signed than finished`
                : net < 0
                  ? `${-net} more finished than signed`
                  : 'in step'}
            </Callout>
          </div>
          <TrendChart
            series={series} short={labels.short} long={labels.long}
            fields={[
              { key: 'signed', name: 'Clients signed', color: HUES.sky, get: (b) => b.signed },
              { key: 'fotcs', name: 'FOTCs completed', color: HUES.emerald, get: (b) => b.fotcs },
            ]}
          />
        </div>
      </Card>

      {/* Calls (Nic 2026-10-25): its own card. */}
      <CallsCard calls={data?.calls} team="onboarding" period={period} />

      <Card eyebrow={periodLabel(period)} title="By specialist" info="This period: hot seats they took, FOTCs credited to the client's specialist, how long those took, and who is Dead now. Right now: what they are carrying.">
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            {/* Two stories, named, like Sales By rep (Nic 2026-11-02): what the
                specialist did THIS PERIOD (hot seats taken, FOTCs, how long they
                took, who died) and what they are carrying RIGHT NOW. Percent
                pills with the counts on hover, the Specialist column pinned
                while the rest scrolls on a narrow screen. */}
            <thead>
              <tr>
                <th className={cn('sticky left-0 z-10', SURFACE)} />
                <th colSpan={4} className="px-2 pb-0 pt-1">
                  <span className="block border-b-2 border-emerald-400 pb-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:border-emerald-500/70 dark:text-slate-400">
                    This period
                  </span>
                </th>
                <th className="w-4 p-0" />
                <th colSpan={3} className="px-2 pb-0 pt-1">
                  <span className="block border-b-2 border-sky-400 pb-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:border-sky-500/70 dark:text-slate-400">
                    Right now
                  </span>
                </th>
              </tr>
              <tr className="border-b border-slate-100 dark:border-white/10">
                <Th align="center" className={cn('sticky left-0 z-10 pl-5', SURFACE)}>Specialist</Th>
                <Th align="center" className="px-2" info="Hot seats they answered live, as a share of the ones they took; the rest became callbacks. Hover for the counts.">Hot seats</Th>
                <Th align="center" className="px-2" info="First Ready for a Claims Manager per client, credited to the client's specialist. Underneath: any credited ones.">FOTCs</Th>
                <Th align="center" className="px-2" info="Median days from signing to FOTC, for their FOTCs in this period.">Signed to FOTC</Th>
                <Th align="center" className="px-2" info="Of the clients signed in this period, how many are Dead now, credited to their specialist. Bringing one back takes it off. Hover for the counts.">Dead</Th>
                <th className="w-4 p-0" />
                <Th align="center" className="px-2" info="Clients they're working right now.">Working</Th>
                <Th align="center" className="px-2" info="Of those, how many are waiting on VA.gov.">On VA.gov</Th>
                <Th align="center" info="Of those, how many are waiting on just their private treatment records (everything else is in)." className="pl-2 pr-5">On PTRs</Th>
              </tr>
            </thead>
            <tbody>
              {people.length ? (
                people.map((s) => {
                  const hs = (s.hotSeat?.live || 0) + (s.hotSeat?.callback || 0);
                  const livePct = hs ? Math.round(((s.hotSeat.live || 0) / hs) * 100) : null;
                  return (
                    <tr key={s.userId} className="border-b border-slate-100 last:border-0 dark:border-white/5">
                      <td className={cn('sticky left-0 z-10 py-3 pl-5 pr-2 text-center', SURFACE)}>
                        {/* Initials only, like Sales By rep (Nic 2026-10-26); the name on hover. */}
                        <span className="inline-flex" title={s.name} aria-label={s.name}>
                          <Avatar name={s.name} color={colors[s.userId]} />
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        {hs ? (
                          <span title={`${s.hotSeat.live || 0} live of ${hs} taken${s.hotSeat.callback ? `, ${s.hotSeat.callback} callback` : ''}`}>
                            <GoalPill value={livePct} target={hotSeatTarget}>
                              {fmtPct(livePct)}
                            </GoalPill>
                          </span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-base font-semibold tabular-nums text-slate-900 dark:text-white">{s.fotcs}</span>
                          {s.fotcsCredited ? (
                            <span className="text-[10px] tabular-nums text-slate-400">{s.fotcsCredited} credited</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums text-slate-700 dark:text-slate-200">
                        {fmtDays(s.medianDaysToFotc)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        {s.signed ? (
                          <span
                            title={`${s.dead} of ${s.signed} signed in this period are Dead now`}
                            className={cn(
                              'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
                              s.dead
                                ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
                                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
                            )}
                          >
                            {fmtPct(s.deadPct)}
                          </span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="w-4 p-0" />
                      <td className="whitespace-nowrap px-2 py-3 text-center text-base font-semibold tabular-nums text-slate-900 dark:text-white">{s.working}</td>
                      <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums">
                        {s.waitingOnVaGov ? (
                          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">{s.waitingOnVaGov}</span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-3 pl-2 pr-5 text-center tabular-nums">
                        {s.waitingOnPtrs ? (
                          <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">{s.waitingOnPtrs}</span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-xs text-slate-400">
                    No Onboarding Specialists yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Card
          className="xl:col-span-2"
          eyebrow="Right now"
          title="Where clients are stuck"
          info="For each client being worked now, the first checklist step that isn't done yet."
        >
          {data.stuckOn?.length ? (
            <div className="space-y-3">
              {data.stuckOn.map((s) => (
                <div key={s.stepId}>
                  <div className="mb-1 flex justify-between gap-3 text-xs">
                    <span className="text-slate-700 dark:text-slate-200">{s.title}</span>
                    <span className="font-semibold tabular-nums text-slate-900 dark:text-white">{s.clients}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 dark:bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-400 transition-[width] duration-700"
                      style={{ width: `${(s.clients / stuckMax) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 py-8 text-center text-sm text-slate-400 dark:bg-white/5">
              Nobody is being worked right now.
            </div>
          )}
        </Card>

        <Card
          className="xl:col-span-3"
          eyebrow={`${periodLabel(period)} · ${va?.clients ?? 0} signed`}
          title="VA.gov access"
          info="For clients who signed in this period: what the intake said, what the Sales Rep confirmed, and what was true on the onboarding call, as three bars of the same clients. The headline is the last one."
        >
          {va ? (
            <>
              {/* The headline is the truth (Option A, Nic 2026-11-02): the
                  share who could sign in once onboarding checked, with its
                  two exceptions beside it, never four equal tiles. */}
              <div className="mb-3 grid gap-x-6 gap-y-3 rounded-xl bg-slate-50 px-4 py-3.5 dark:bg-white/5 md:grid-cols-[auto_1fr] md:items-center">
                <div>
                  <div className="text-[28px] font-semibold leading-none tabular-nums text-emerald-600 dark:text-emerald-400">
                    {fmtPct(va.pct.hadAccess)}
                  </div>
                  <div className="mt-1.5 text-[11.5px] leading-snug text-slate-500 dark:text-slate-400">
                    could sign in when onboarding checked
                    <br />
                    <b className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                      {va.onboarding.yes} of {va.onboarding.yes + va.onboarding.no}
                    </b>{' '}
                    checked so far
                  </div>
                </div>
                <div className="grid gap-1 text-xs text-slate-500 dark:text-slate-400">
                  <span>
                    <b className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">{va.saidYesButNo}</b> said yes to the rep but couldn&apos;t sign in
                    {va.pct.saidYesButNo != null ? <span className="tabular-nums"> ({fmtPct(va.pct.saidYesButNo)})</span> : null}
                  </span>
                  <span>
                    <b className={cn('font-semibold tabular-nums', va.waitingNow ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-slate-200')}>{va.waitingNow}</b> still waiting on VA.gov right now
                  </span>
                  {va.medianDaysOnHold != null ? (
                    <span>
                      <b className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">{fmtDays(va.medianDaysOnHold)}</b> median wait to get set up
                    </span>
                  ) : null}
                </div>
              </div>
              {va.readyButNot && (va.readyButNot.sales || va.readyButNot.client) ? (
                <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 rounded-xl bg-amber-50 px-3.5 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                  <span>
                    Said ready but wasn&apos;t: <b className="tabular-nums">{va.readyButNot.sales}</b> by the Sales Rep
                  </span>
                  <span>
                    <b className="tabular-nums">{va.readyButNot.client}</b> by the client
                  </span>
                </div>
              ) : null}
              <div className="grid gap-3">
                <VaBar title="Intake said" sub="When they came in" buckets={va.intake} rows={VA_ROWS} />
                <VaBar title="Sales Rep confirmed" sub="On the call" buckets={va.confirmed} rows={VA_ROWS} />
                <VaBar
                  title="Onboarding call"
                  sub="What was actually true"
                  buckets={{ yes: va.onboarding.yes, no: va.onboarding.no, unknown: va.onboarding.notChecked }}
                  rows={[
                    { key: 'yes', label: 'Could sign in' },
                    { key: 'no', label: "Couldn't sign in" },
                    { key: 'unknown', label: 'Not checked yet' },
                  ]}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                <span><i className={cn('mr-1.5 inline-block h-2 w-2 rounded-sm align-[-1px]', VA_CLASS.yes)} />Yes / could sign in</span>
                <span><i className={cn('mr-1.5 inline-block h-2 w-2 rounded-sm align-[-1px]', VA_CLASS.no)} />No / couldn&apos;t</span>
                <span><i className={cn('mr-1.5 inline-block h-2 w-2 rounded-sm align-[-1px]', VA_CLASS.unsure)} />Didn&apos;t know</span>
                <span><i className={cn('mr-1.5 inline-block h-2 w-2 rounded-sm align-[-1px]', VA_CLASS.unknown)} />Not asked / not checked</span>
              </div>
            </>
          ) : null}
        </Card>
      </div>

      <BonusLadder bonus={data.bonus} unit="FOTCs" colors={colors} allSources={filtered} />
    </div>
  );
};

export default OnboardingTeamTab;
